import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";
import { useChangelog } from "../../hooks/useDocs";
import { compareReleaseIds } from "../../models/Docs";
import { EntriesByCategory, formatReleaseDate } from "./ChangelogView";

/** Releases shown in full; any older unseen ones are only counted, the changelog has them. */
const MAX_RELEASES = 3;

const shownKey = (userId: string) => `nfs.whatsNewShown.${userId}`;

const readShown = (userId: string | null): string | undefined => {
  if (!userId) return undefined;
  try {
    return localStorage.getItem(shownKey(userId)) ?? undefined;
  } catch {
    return undefined;
  }
};

const writeShown = (userId: string | null, release: string) => {
  if (!userId) return;
  try {
    localStorage.setItem(shownKey(userId), release);
  } catch {
    // Storage blocked: the dialog stays closed for this session and shows again on the next visit.
  }
};

interface WhatsNewDialogProps {
  unseenReleases: number;
  latestRelease?: string;
  docsBasePath: string;
}

/**
 * Announces the releases a user has not seen, once per release, the next time they open the portal.
 *
 * Closing it does not record the release as seen: the dot on What's new and the count next to Docs stay until the
 * user opens the changelog, which is what records it. That the dialog was shown is remembered in this browser only,
 * so a user on a second browser sees it once more there.
 */
const WhatsNewDialog: React.FC<WhatsNewDialogProps> = ({ unseenReleases, latestRelease, docsBasePath }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getUserId } = useAuth();
  const userId = getUserId();
  const onChangelog = location.pathname.endsWith("/docs/changelog");
  const [shownFor, setShownFor] = useState(() => readShown(userId));
  const announce = unseenReleases > 0 && !!latestRelease && compareReleaseIds(latestRelease, shownFor) > 0;
  const { data } = useChangelog(announce && !onChangelog);
  const openedAt = useRef<string | undefined>(undefined);

  const unseen = (data?.releases ?? []).filter((release) => compareReleaseIds(release.id, data?.lastSeenRelease) > 0);
  const open = announce && unseen.length > 0 && !onChangelog;

  const close = () => {
    if (!latestRelease) return;
    setShownFor(latestRelease);
    writeShown(userId, latestRelease);
  };

  const openChangelog = () => {
    close();
    navigate(`${docsBasePath}/changelog`);
  };

  // A link in an entry navigates under the dialog; following one closes it.
  useEffect(() => {
    if (!open) {
      openedAt.current = undefined;
      return;
    }
    if (openedAt.current === undefined) openedAt.current = location.pathname;
    else if (openedAt.current !== location.pathname) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, location.pathname]);

  if (!open) return null;

  const shown = unseen.slice(0, MAX_RELEASES);
  const older = unseen.length - shown.length;

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && close()}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-5" />
            What's new in NFS
          </DialogTitle>
          <DialogDescription>
            {unseen.length === 1
              ? "A release has shipped since you last opened What's new."
              : `${unseen.length} releases have shipped since you last opened What's new.`}
          </DialogDescription>
        </DialogHeader>

        <div className="-mx-6 min-h-0 flex-1 overflow-y-auto border-y px-6 py-2">
          {shown.map((release) => {
            const entries = release.sections.flatMap((section) => section.entries);
            const actionRequired = entries.filter((entry) => entry.impact === "action-required").length;
            return (
              <section key={release.id} className="border-t py-3 first:border-t-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold">{formatReleaseDate(release.date)}</h2>
                  {actionRequired > 0 && <Badge variant="danger">{actionRequired} action required</Badge>}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{release.summary}</p>
                {entries.length > 0 && <EntriesByCategory entries={entries} baseSlug={`changelog/${release.id}`} />}
              </section>
            );
          })}
          {older > 0 && (
            <p className="py-3 text-sm text-muted-foreground">
              {older === 1 ? "One older release" : `${older} older releases`} — open What's new to see{" "}
              {older === 1 ? "it" : "them"}.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={close}>Dismiss</Button>
          <Button onClick={openChangelog}>
            <Sparkles />
            Open What's new
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewDialog;
